import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Truck,
  X,
  Bot,
  Boxes,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  PackageOpen,
  Package,
  Scissors,
  Factory,
  Wrench,
  BarChart,
  UserCog,
  ListTodo,
  Briefcase,
  MessageSquare,
  DollarSign,
  Wallet,
  Calculator,
  Settings,
  FileText,
  Shield,
  Crown,
  Award,
  UserCheck,
  Activity,
  Mail,
  Calendar,
  Star,
  AlertTriangle,
  TrendingUp,
  LineChart
} from 'lucide-react';
import { useAppStore } from '../../store';
import { useAuth } from '../../context/AuthContext';

interface SidebarProps {
  isMobileOpen: boolean;
  toggleMobileSidebar: () => void;
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

interface MenuItem {
  name: string;
  icon?: React.ElementType;
  href?: string;
  items?: {
    name: string;
    href: string;
    icon: React.ElementType;
    roles?: string[];
  }[];
  roles?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, toggleMobileSidebar, isCollapsed, toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const { darkMode } = useAppStore();
  const { userRole, isAdmin } = useAuth();
  
  const navigation: MenuItem[] = [
    {
      name: 'Gestión',
      icon: Briefcase,
      roles: ['Administrador', 'Producción', 'Vendedor'],
      items: [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['Administrador'] },
        { name: 'Analítica', href: '/analitica', icon: LineChart, roles: ['Administrador'] },
        { name: 'Clientes', href: '/clientes', icon: Users, roles: ['Administrador', 'Vendedor'] },
        { name: 'Estado de Resultados', href: '/estado-resultados', icon: TrendingUp, roles: ['Administrador'] },
        { name: 'Remitos', href: '/remitos', icon: FileText, roles: ['Administrador', 'Producción'] },
        { name: 'Acuerdos de Servicio', href: '/acuerdos-servicio', icon: FileText, roles: ['Administrador', 'Vendedor'] },
        { name: 'Producción', href: '/productionadmin', icon: BarChart, roles: ['Administrador'] },
        { name: 'Gastos', href: '/gastos', icon: DollarSign, roles: ['Administrador', 'Producción'] },
        { name: 'Actividad', href: '/actividad', icon: Activity, roles: ['Administrador'] },
        { name: 'Actividad Clientes', href: '/actividad-clientes', icon: Activity, roles: ['Administrador'] },
        { name: 'Proveedores', href: '/proveedores', icon: Truck, roles: ['Administrador', 'Producción'] },
        { name: 'Facturas', href: '/facturas', icon: FileText, roles: ['Administrador'] },
        { name: 'Reportes', href: '/reportes', icon: BarChart, roles: ['Administrador'] },
        { name: 'Tesorería', href: '/tesoreria', icon: Wallet, roles: ['Administrador'] },
        { name: 'Cotizador', href: '/cotizador', icon: Calculator, roles: ['Administrador', 'Vendedor'] },
        { name: 'Asistente', href: '/asistente', icon: Bot, roles: ['Administrador'] },
        { name: 'Usuarios', href: '/usuarios', icon: UserCog, roles: ['Administrador'] },
        { name: 'Portales de Clientes', href: '/portales-clientes', icon: Shield, roles: ['Administrador'] },
        { name: 'Club Productex®', href: '/club-productex-config', icon: Crown, roles: ['Administrador'] },
        { name: 'Chat', href: '/chat', icon: MessageSquare, roles: ['Administrador', 'Vendedor', 'Producción', 'Depósito'] },
      ]
    },
    {
      name: 'Equipo',
      icon: Users,
      roles: ['Administrador', 'Super Admin', 'Vendedor', 'Producción', 'Depósito'],
      items: [
        { name: 'Colaboradores', href: '/colaboradores', icon: Users, roles: ['Administrador'] },
        { name: 'Puntos Colaboradores', href: '/puntos-colaboradores-config', icon: Award, roles: ['Administrador', 'Super Admin'] },
        { name: 'Mis Puntos', href: '/puntos-colaboradores', icon: Award, roles: ['Administrador', 'Vendedor', 'Producción', 'Depósito'] },
        { name: 'Proyectos', href: '/todolist', icon: ListTodo, roles: ['Administrador'] },
      ]
    },
    {
      name: 'Contenido',
      icon: Calendar,
      roles: ['Administrador', 'Contenido'],
      items: [
        { name: 'Calendario', href: '/contenido/calendario', icon: Calendar, roles: ['Administrador', 'Contenido'] },
        { name: 'Informes', href: '/contenido/informes', icon: FileText, roles: ['Administrador', 'Contenido'] },
      ]
    },
    {
      name: 'Atención',
      icon: UserCheck,
      roles: ['Administrador', 'Super Admin', 'Vendedor'],
      items: [
        { name: 'Reclamos', href: '/reclamos', icon: AlertTriangle, roles: ['Administrador', 'Super Admin'] },
        { name: 'Generar reclamo', href: '/generar-reclamo', icon: AlertTriangle, roles: ['Administrador', 'Vendedor'] },
        { name: 'Seguimiento', href: '/seguimiento', icon: UserCheck, roles: ['Administrador', 'Vendedor'] },
        { name: 'Encuestas Satisfacción', href: '/encuestas-satisfaccion', icon: Star, roles: ['Administrador', 'Super Admin'] },
      ]
    },
    {
      name: 'Depósito',
      icon: Package,
      roles: ['Administrador', 'Depósito'],
      items: [
        { name: 'Pedidos', href: '/pedidos', icon: Package, roles: ['Administrador', 'Depósito'] }
      ]
    },
    {
      name: 'Express',
      icon: PackageOpen,
      roles: ['Administrador', 'Vendedor', 'Producción', 'Depósito'],
      items: [
        { name: 'Producciones', href: '/express', icon: PackageOpen, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Muestras', href: '/express/muestras', icon: Scissors, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Stock', href: '/express/stock', icon: Package, roles: ['Administrador', 'Vendedor', 'Producción', 'Depósito'] },
      ]
    },
    {
      name: 'PRO',
      icon: Factory,
      roles: ['Administrador', 'Vendedor', 'Producción'],
      items: [
        { name: 'Producciones', href: '/producciones', icon: Boxes, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Muestras', href: '/muestras', icon: Scissors, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Aprobación de Fichas', href: '/fichas', icon: FileText, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Cotizador', href: '/cotizador', icon: Calculator, roles: ['Administrador', 'Vendedor'] },
      ]
    },
    {
      name: 'Producción',
      icon: Wrench,
      roles: ['Administrador', 'Producción'],
      items: [
        { name: 'Manager', href: '/productionmanager', icon: Wrench, roles: ['Administrador', 'Producción'] },
        { name: 'Logística', href: '/logistics', icon: Truck, roles: ['Administrador', 'Producción'] },
        { name: 'Talleres', href: '/talleres', icon: Factory, roles: ['Administrador', 'Producción'] },
        { name: 'Calendario Talleres', href: '/calendario-talleres', icon: Calendar, roles: ['Administrador', 'Producción'] },
        { name: 'Cotizador', href: '/cotizador', icon: Calculator, roles: ['Administrador', 'Producción'] },
        { name: 'To-Do Producción', href: '/production-todos', icon: ListTodo, roles: ['Administrador', 'Producción'] },
      ]
    },
    {
      name: 'Configuración',
      icon: Settings,
      roles: ['Administrador'],
      items: [
        { name: 'General', href: '/configuracion', icon: Settings, roles: ['Administrador'] },
        { name: 'Cotizador', href: '/configuracion/cotizador', icon: Calculator, roles: ['Administrador'] },
        { name: 'Plantillas Email', href: '/email-templates', icon: Mail, roles: ['Administrador'] },
      ]
    }
  ];

  const isActive = (path: string) => location.pathname === path;
  
  // Find which group should be open based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Find which group contains the current path
    for (const group of filteredNavigation) {
      if (group.items) {
        const matchingItem = group.items.find(item => item.href === currentPath);
        if (matchingItem) {
          setOpenGroup(group.name);
          break;
        }
      }
    }
  }, [location.pathname]);
  
  const toggleGroup = (groupName: string) => {
    // If clicking the same group, toggle it
    if (openGroup === groupName) {
      setOpenGroup(null);
    } else {
      // Otherwise, open the clicked group and close any other
      setOpenGroup(groupName);
    }
  };

  const isGroupOpen = (groupName: string) => openGroup === groupName;

  const handleNavigation = (href: string) => {
    navigate(href);
    if (isMobileOpen) {
      toggleMobileSidebar();
    }
  };

  const handleMouseEnter = (itemName: string, event: React.MouseEvent) => {
    if (!isCollapsed) return;
    
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredItem(itemName);
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 8
    });
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  // For administrators, show all menu items without filtering
  // For other roles, filter based on role permissions
  const getFilteredNavigation = () => {
    // Items to hide from sidebar but keep accessible via search
    const hiddenItems = [
      'Proveedores', 
      'Facturas', 
        'Reportes', 
      'Tesorería', 
      'Cotizador', 
      'Asistente', 
      'Chat',
      'Usuarios',
      'Portales de Clientes',
      'Club Productex®'
    ];

    // Admin sees everything - no filtering
    if (isAdmin) {
      return navigation.map(group => ({
        ...group,
        items: group.items?.filter(item => !hiddenItems.includes(item.name))
      })).filter(group => group.items && group.items.length > 0);
    }
    
    // For non-admin users, filter based on role
    return navigation
      .filter(group => {
        // If group has roles defined, check if user has one of those roles
        if (group.roles && userRole) {
          return group.roles.includes(userRole);
        }
        // If no roles defined for group, show to everyone
        return true;
      })
      .map(group => ({
        ...group,
        // Filter items within group based on user role and hidden items
        items: group.items?.filter(item => {
          // Hide specific items from sidebar
          if (hiddenItems.includes(item.name)) {
            return false;
          }
          
          // If item has roles defined, check if user has one of those roles
          if (item.roles && userRole) {
            // Special case: Allow Producción role to access Proveedores
            if (userRole === 'Producción' && item.name === 'Proveedores') {
              return true;
            }
            return item.roles.includes(userRole);
          }
          // If no roles defined for item, show to everyone
          return true;
        })
      }))
      .filter(group => group.items && group.items.length > 0); // Remove empty groups
  };

  const filteredNavigation = getFilteredNavigation();

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 z-20 bg-gray-900/50 lg:hidden" 
          onClick={toggleMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 z-30 h-full bg-black text-white transform transition-all duration-300 ease-in-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 lg:static lg:z-0
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
          <div className={`flex items-center ${isCollapsed ? 'justify-center flex-1' : ''}`}>
            {!isCollapsed && (
              <span className="text-xl font-bold tracking-wider">PRODUCTEX</span>
            )}
            {isCollapsed && (
              <span className="text-base font-bold tracking-wider">PTX</span>
            )}
          </div>
          <button 
            onClick={toggleMobileSidebar} 
            className="lg:hidden text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className={`h-[calc(100vh-64px)] overflow-y-auto overflow-x-hidden scrollbar-hide ${isCollapsed ? 'p-2' : 'p-4'}`}>
          <div className="space-y-2">
            {filteredNavigation.map((group) => (
              <div key={group.name} className="border-b border-gray-800 pb-2 last:border-0">
                {isCollapsed ? (
                  // Modo colapsado - solo mostrar iconos
                  <div className="space-y-1">
                    {group.items && group.items.map((item) => {
                      const active = isActive(item.href);
                      return (
                        <button
                          key={item.name}
                          onClick={() => handleNavigation(item.href)}
                          onMouseEnter={(e) => handleMouseEnter(item.name, e)}
                          onMouseLeave={handleMouseLeave}
                          className={`
                            flex items-center justify-center w-full p-2 text-sm rounded-md transition-colors
                            ${active 
                              ? 'bg-gray-800 text-white' 
                              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                            }
                          `}
                        >
                          <item.icon className="h-5 w-5" />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  // Modo expandido - mostrar texto completo
                  <>
                    <button
                      onClick={() => toggleGroup(group.name)}
                      className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors group"
                    >
                      <div className="flex items-center">
                        <div className="p-1.5 bg-black rounded-lg mr-3">
                          {group.icon && <group.icon className="h-4 w-4 text-white" />}
                        </div>
                        <span className="font-semibold uppercase tracking-wider">
                          {group.name}
                        </span>
                      </div>
                      {group.items && group.items.length > 0 && (
                        <div className="text-gray-500 group-hover:text-white">
                          {isGroupOpen(group.name) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </div>
                      )}
                    </button>
                    
                    {group.items && group.items.length > 0 && isGroupOpen(group.name) && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {group.items.map((item) => {
                          const active = isActive(item.href);
                          return (
                            <li key={item.name}>
                              <button
                                onClick={() => handleNavigation(item.href)}
                                className={`
                                  flex items-center w-full px-4 py-1 text-sm rounded-md transition-colors text-left
                                  ${active 
                                    ? 'bg-gray-800 text-white' 
                                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                                  }
                                `}
                              >
                                <div className="p-1.5 bg-black rounded-lg mr-3">
                                  <item.icon className="h-4 w-4 text-white" />
                                </div>
                                {item.name}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </nav>
      </div>

      {/* Global Tooltip */}
      {isCollapsed && hoveredItem && (
        <div
          className="fixed px-3 py-2 bg-gray-900 text-white text-sm rounded shadow-lg pointer-events-none whitespace-nowrap z-[9999]"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)'
          }}
        >
          {hoveredItem}
        </div>
      )}
    </>
  );
};

export default Sidebar;